// TableComponent.styles.ts
import { device } from "@src/theme";
import styled from "styled-components";

export const TableWrapper = styled.div<{
  variant: "darkBg" | "grayBg" | "inherit";
}>`
  width: 100%;
  /* Allow this flex item to shrink to its container instead of expanding to the
     table's intrinsic (many-column) width, which would overflow the panel and
     clip columns off the right edge. */
  min-width: 0;
  overflow-x: hidden;
  background-color: ${(props): string =>
    props.variant === "darkBg"
      ? props.theme.colors.darkBg
      : props.variant === "grayBg"
        ? props.theme.colors.gray300
        : "inherit"};
  font-weight: 500;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  color: ${(props): string => props.theme.colors.gray100};
  table-layout: fixed;
  font-weight: 500;
  position: relative;
`;

export const TableHeader = styled.thead<{
  variant?: "darkBg" | "grayBg" | "inherit";
}>`
  background-color: inherit;
  border-bottom: 1px solid
    ${(props): string =>
      props.variant === "darkBg"
        ? props.theme.colors.gray400
        : props.variant === "grayBg"
          ? props.theme.colors.gray200
          : "transparent"};
  position: sticky;
  top: 0;
  transition:
    max-height 0.3s ease,
    height 0.3s ease,
    width 0.3s ease;
  z-index: 2;
  &:hover {
    border-bottom: 1px solid
      ${(props): string =>
        props.variant === "darkBg"
          ? props.theme.colors.gray400
          : props.variant === "grayBg"
            ? props.theme.colors.gray200
            : "transparent"};
  }
`;

export const TableHeaderCell = styled.th<{
  $centerValues?: boolean;
  $compactUI?: boolean;
  $index: number;
  variant?: "darkBg" | "grayBg" | "inherit";
}>`
  background-color: ${(props): string =>
    props.variant === "darkBg"
      ? props.theme.colors.darkBg
      : props.variant === "grayBg"
        ? props.theme.colors.gray300
        : "inherit"};
  user-select: none;
  height: ${(props) => (props.$compactUI ? "60px" : "85px")};
  font-weight: bold;
  text-align: ${(props) => (props.$centerValues ? "center" : "left")};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) => props.theme.colors.yellow};
  padding-left: ${(props) =>
    props.$index === 0 ? props.theme.margins.xl : props.theme.margins.small};
  padding-right: ${(props) => props.theme.margins.small};
  transition:
    max-height 0.3s ease,
    height 0.3s ease,
    width 0.3s ease;
  @media ${device.tablet} {
    padding-left: ${(props) => props.theme.margins.small};
    padding-right: ${(props) => props.theme.margins.small};
  }
  @media ${device.laptop} {
    padding-left: ${(props) =>
      props.$index === 0
        ? props.theme.margins.large
        : props.theme.margins.small};
    padding-right: ${(props) => props.theme.margins.small};
  }
  width: ${(props) =>
    props.$index === 0 || props.$index === 1 ? "min-content" : "auto"};

  background-color: inherit;
  border-bottom: none;
  flex-direction: row;
  position: relative;
`;

export const ColumnResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 10px;
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  display: flex;
  justify-content: center;
  z-index: 3;

  &::after {
    content: "";
    width: 2px;
    height: 55%;
    align-self: center;
    background-color: transparent;
    transition: background-color 0.15s ease;
  }

  &:hover::after,
  &.resizing::after {
    background-color: ${(props) => props.theme.colors.yellow};
  }
`;

export const TableRow = styled.tr<{
  variant?: "darkBg" | "grayBg" | "inherit";
  activeRow?: boolean;
}>`
  outline: ${(props): string =>
    props.activeRow ? `1px solid ${props.theme.colors.yellow}` : "none"};
  background-color: ${(props): string =>
    props.activeRow
      ? props.theme.colors.darkGray
      : props.variant === "darkBg"
        ? props.theme.colors.darkBg
        : props.variant === "grayBg"
          ? props.theme.colors.gray300
          : "inherit"};
  &:hover {
    outline: 1px solid ${(props): string => props.theme.colors.yellow};
    background-color: ${(props): string => props.theme.colors.darkGray};

    th {
      outline: none;
    }
  }
  thead &:hover {
    outline: none;
    background-color: ${(props): string =>
      props.variant === "darkBg"
        ? props.theme.colors.darkBg
        : props.variant === "grayBg"
          ? props.theme.colors.gray300
          : "inherit"};
  }
  transition:
    max-height 0.3s ease,
    height 0.3s ease,
    width 0.3s ease;
  z-index: 0;
`;

export const TableCell = styled.td<{
  $textOverflow?: string;
  $centerValues?: boolean;
  $compactUI?: boolean;
  $index: number;
  $expandedHeight?: string;
  variant?: "darkBg" | "grayBg" | "inherit";
}>`
  height: ${(props) =>
    props.$compactUI ? "60px" : (props.$expandedHeight ?? "85px")};
  max-height: ${(props) =>
    props.$compactUI ? "60px" : (props.$expandedHeight ?? "85px")};

  overflow: hidden;
  text-overflow: ${(props) =>
    props.$textOverflow ? props.$textOverflow : "ellipsis"};
  white-space: nowrap;

  text-align: ${(props) => (props.$centerValues ? "center" : "left")};
  border-bottom: 1px solid
    ${(props): string =>
      props.variant === "darkBg"
        ? props.theme.colors.gray400
        : props.variant === "grayBg"
          ? props.theme.colors.gray200
          : "transparent"};
  font-weight: 500;

  width: ${(props) =>
    props.$index === 0 || props.$index === 1 ? "min-content" : "auto"};

  padding-left: ${(props) =>
    props.$index === 0 ? props.theme.margins.xl : props.theme.margins.small};
  padding-right: ${(props) => props.theme.margins.small};
  transition:
    max-height 0.3s ease,
    height 0.3s ease,
    width 0.3s ease;

  @media ${device.tablet} {
    padding-left: ${(props) => props.theme.margins.small};
    padding-right: ${(props) => props.theme.margins.small};
  }

  @media ${device.laptop} {
    padding-left: ${(props) =>
      props.$index === 0
        ? props.theme.margins.large
        : props.theme.margins.small};
    padding-right: ${(props) => props.theme.margins.small};
  }
`;

export const TableActionsGroup = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${(props): string => props.theme.margins.small};

  @media ${device.mobile} {
    gap: 0;
  }
  @media ${device.tablet} {
    gap: 0;
  }
  @media ${device.laptop} {
    gap: 0;
  }
`;
